import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { LoadState } from '../../components/workflowShared';
import type { AccountUser, Workspace } from '../../components/screens/RunScreens';
import type { AppChat } from './appChatStore';
import AppMenu, { type AppMenuItem } from './AppMenu';
import {
  IconCard,
  IconChat,
  IconChevronRight,
  IconCompose,
  IconContext,
  IconFolder,
  IconFolderPlus,
  IconGithub,
  IconGrid,
  IconIssues,
  IconLogOut,
  IconMore,
  IconPlus,
  IconRename,
  IconScope,
  IconSettings,
  IconSidebar,
  IconTrash,
  IconWorkflow
} from './appIcons';
import type { AppGitHubState } from './useAppAccount';

export type AppView = 'chat' | 'scope' | 'context' | 'issues' | 'tools' | 'workflows' | 'pricing' | 'settings';

interface AppSidebarProps {
  view: AppView;
  activeChatId: string | null;
  activeWorkspaceId: string;
  workspaces: Workspace[];
  workspacesState: LoadState;
  chats: AppChat[];
  expandedWorkspaceIds: Set<string>;
  /** Open findings in the active workspace's register; other workspaces have none loaded. */
  openIssueCount?: number;
  /** Short name of the repository a workspace's scope points at, when known. */
  getScopeLabel: (workspace: Workspace) => string | undefined;
  /** Preconditions, trust assumptions and documents saved for a workspace. */
  getContextCount: (workspaceId: string) => number;
  isCreatingWorkspace: boolean;
  githubState: AppGitHubState;
  accountUser: AccountUser | null;
  isSignedIn: boolean;
  githubLogin: string;
  planName: string;
  onToggleSidebar: () => void;
  onNewChat: () => void;
  onStartCreateWorkspace: () => void;
  onCancelCreateWorkspace: () => void;
  onCreateWorkspace: (name: string) => Promise<boolean>;
  onRenameWorkspace: (workspaceId: string, name: string) => Promise<boolean>;
  onDeleteWorkspace: (workspaceId: string) => void;
  onConnectGitHub: () => void;
  onOpenTools: () => void;
  onOpenWorkflows: () => void;
  onOpenPlans: () => void;
  onOpenSettings: () => void;
  onToggleWorkspace: (workspaceId: string) => void;
  onNewChatInWorkspace: (workspaceId: string) => void;
  onSelectScope: (workspaceId: string) => void;
  onSelectContext: (workspaceId: string) => void;
  onSelectIssues: (workspaceId: string) => void;
  onSelectChat: (chat: AppChat) => void;
  onRenameChat: (chatId: string, title: string) => void;
  onDeleteChat: (chatId: string) => void;
  onLogin: () => void;
  onLogout: () => void;
}

interface OpenMenu {
  anchor: HTMLElement;
  items: AppMenuItem[];
  placement?: 'below-end' | 'above-stretch';
}

function InlineNameInput({
  initialValue = '',
  placeholder,
  icon,
  onSubmit,
  onCancel
}: {
  initialValue?: string;
  placeholder: string;
  icon: ReactNode;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  // Unmounting after Enter/Escape can still fire blur; only the first outcome counts.
  const isSettledRef = useRef(false);

  const settle = (action: () => void) => {
    if (!isSettledRef.current) {
      isSettledRef.current = true;
      action();
    }
  };

  const commit = () => settle(() => {
    if (value.trim()) {
      onSubmit(value.trim());
    } else {
      onCancel();
    }
  });

  return (
    <div className="app-row editing">
      <span className="app-row-icon">{icon}</span>
      <input
        className="app-row-input"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={true}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            settle(onCancel);
          }
        }}
      />
    </div>
  );
}

function AppAvatar({ user, login }: { user: AccountUser | null; login: string }) {
  const [hasImageError, setHasImageError] = useState(false);
  const name = user?.githubLogin || login;

  if (user?.githubAvatarUrl && !hasImageError) {
    return <img className="app-avatar" src={user.githubAvatarUrl} alt="" onError={() => setHasImageError(true)} />;
  }

  return <span className="app-avatar" aria-hidden={true}>{(name || '?').charAt(0).toUpperCase()}</span>;
}

function AppSidebar(props: AppSidebarProps) {
  const {
    view,
    activeChatId,
    activeWorkspaceId,
    workspaces,
    workspacesState,
    chats,
    expandedWorkspaceIds,
    openIssueCount,
    isCreatingWorkspace,
    githubState,
    accountUser,
    isSignedIn,
    githubLogin
  } = props;
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renamingWorkspaceId, setRenamingWorkspaceId] = useState<string | null>(null);
  const displayName = accountUser?.githubLogin || githubLogin || 'Account';

  const toggleMenu = (anchor: HTMLElement, items: AppMenuItem[], placement?: OpenMenu['placement']) => {
    setOpenMenu((current) => current?.anchor === anchor ? null : { anchor, items, placement });
  };

  return (
    <aside className="app-sidebar" aria-label="Sidebar">
      <div className="app-sidebar-inner">
        <div className="app-sidebar-head">
          <a className="app-brand" href="/" aria-label="Havu home">
            <img src="/logoinvert.png" alt="" />
            <span>Havu</span>
          </a>
          <button type="button" className="app-icon-button" aria-label="New chat" title="New chat" onClick={props.onNewChat}>
            <IconCompose aria-hidden={true} />
          </button>
          <button type="button" className="app-icon-button" aria-label="Close sidebar" title="Close sidebar" onClick={props.onToggleSidebar}>
            <IconSidebar aria-hidden={true} />
          </button>
        </div>

        <nav className="app-nav" aria-label="Actions">
          <button type="button" className="app-row" onClick={props.onStartCreateWorkspace}>
            <span className="app-row-icon"><IconFolderPlus aria-hidden={true} /></span>
            <span className="app-row-label">New application</span>
          </button>
          {/* Signed-in users manage GitHub and plans from the profile menu. */}
          {!isSignedIn && (
            <button
              type="button"
              className="app-row"
              onClick={props.onConnectGitHub}
              disabled={githubState === 'loading'}
            >
              <span className="app-row-icon"><IconGithub aria-hidden={true} /></span>
              <span className="app-row-label">Connect GitHub</span>
            </button>
          )}
          <button
            type="button"
            className={`app-row${view === 'tools' ? ' active' : ''}`}
            aria-current={view === 'tools' ? 'page' : undefined}
            onClick={props.onOpenTools}
          >
            <span className="app-row-icon"><IconGrid aria-hidden={true} /></span>
            <span className="app-row-label">Tools</span>
          </button>
          <button
            type="button"
            className={`app-row${view === 'workflows' ? ' active' : ''}`}
            aria-current={view === 'workflows' ? 'page' : undefined}
            onClick={props.onOpenWorkflows}
          >
            <span className="app-row-icon"><IconWorkflow aria-hidden={true} /></span>
            <span className="app-row-label">Workflows</span>
          </button>
          {!isSignedIn && (
            <button
              type="button"
              className={`app-row${view === 'pricing' ? ' active' : ''}`}
              aria-current={view === 'pricing' ? 'page' : undefined}
              onClick={props.onOpenPlans}
            >
              <span className="app-row-icon"><IconCard aria-hidden={true} /></span>
              <span className="app-row-label">Plans</span>
            </button>
          )}
        </nav>

        <nav className="app-tree" aria-label="Applications">
          {isCreatingWorkspace && (
            <InlineNameInput
              placeholder="Application name"
              icon={<IconFolder aria-hidden={true} />}
              onSubmit={(name) => void props.onCreateWorkspace(name)}
              onCancel={props.onCancelCreateWorkspace}
            />
          )}

          {workspacesState === 'loading' && workspaces.length === 0 && (
            <div className="app-tree-skeleton" aria-hidden={true}>
              <span />
              <span />
              <span />
            </div>
          )}

          {workspaces.map((workspace) => {
            const isExpanded = expandedWorkspaceIds.has(workspace.id);
            const workspaceChats = chats.filter((chat) => chat.workspaceId === workspace.id);
            const isIssuesActive = view === 'issues' && activeWorkspaceId === workspace.id;
            const isScopeActive = view === 'scope' && activeWorkspaceId === workspace.id;
            const isContextActive = view === 'context' && activeWorkspaceId === workspace.id;
            const contextCount = props.getContextCount(workspace.id);
            const scopeLabel = workspace.scope ? props.getScopeLabel(workspace) : 'Not set';
            const issueCount = activeWorkspaceId === workspace.id ? openIssueCount : undefined;

            return (
              <div key={workspace.id} className={`app-ws${isExpanded ? ' expanded' : ''}`}>
                {renamingWorkspaceId === workspace.id ? (
                  <InlineNameInput
                    initialValue={workspace.name}
                    placeholder="Application name"
                    icon={<IconFolder aria-hidden={true} />}
                    onSubmit={(name) => {
                      void props.onRenameWorkspace(workspace.id, name);
                      setRenamingWorkspaceId(null);
                    }}
                    onCancel={() => setRenamingWorkspaceId(null)}
                  />
                ) : <div className={`app-row app-ws-row${openMenu?.anchor.dataset.workspaceId === workspace.id ? ' menu-open' : ''}`}>
                  <button
                    type="button"
                    className="app-row-main"
                    aria-expanded={isExpanded}
                    onClick={() => props.onToggleWorkspace(workspace.id)}
                  >
                    <span className="app-row-icon app-ws-icon">
                      <IconFolder aria-hidden={true} className="app-ws-folder" />
                      <IconChevronRight aria-hidden={true} className="app-ws-chevron" />
                    </span>
                    <span className="app-row-label">{workspace.name}</span>
                  </button>
                  <button
                    type="button"
                    className="app-row-action"
                    aria-label={`New chat in ${workspace.name}`}
                    title="New chat"
                    onClick={() => props.onNewChatInWorkspace(workspace.id)}
                  >
                    <IconPlus aria-hidden={true} />
                  </button>
                  <button
                    type="button"
                    className="app-row-action"
                    aria-label={`Options for ${workspace.name}`}
                    aria-haspopup="menu"
                    data-workspace-id={workspace.id}
                    onClick={(event) => toggleMenu(event.currentTarget, [
                      {
                        label: 'Rename',
                        icon: <IconRename aria-hidden={true} />,
                        onSelect: () => setRenamingWorkspaceId(workspace.id)
                      },
                      {
                        label: 'Delete',
                        icon: <IconTrash aria-hidden={true} />,
                        tone: 'danger',
                        onSelect: () => props.onDeleteWorkspace(workspace.id)
                      }
                    ])}
                  >
                    <IconMore aria-hidden={true} />
                  </button>
                </div>}

                {isExpanded && (
                  <div className="app-ws-children">
                    <button
                      type="button"
                      className={`app-row app-resource-row${isScopeActive ? ' active' : ''}`}
                      aria-current={isScopeActive ? 'page' : undefined}
                      onClick={() => props.onSelectScope(workspace.id)}
                    >
                      <span className="app-row-icon"><IconScope aria-hidden={true} /></span>
                      <span className="app-row-label">Scope</span>
                      {scopeLabel && (
                        <span className={`app-row-meta${workspace.scope ? '' : ' unset'}`}>{scopeLabel}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`app-row app-resource-row${isContextActive ? ' active' : ''}`}
                      aria-current={isContextActive ? 'page' : undefined}
                      onClick={() => props.onSelectContext(workspace.id)}
                    >
                      <span className="app-row-icon"><IconContext aria-hidden={true} /></span>
                      <span className="app-row-label">Context</span>
                      {contextCount > 0 && <span className="app-row-meta">{contextCount}</span>}
                    </button>
                    <button
                      type="button"
                      className={`app-row app-resource-row${isIssuesActive ? ' active' : ''}`}
                      aria-current={isIssuesActive ? 'page' : undefined}
                      onClick={() => props.onSelectIssues(workspace.id)}
                    >
                      <span className="app-row-icon"><IconIssues aria-hidden={true} /></span>
                      <span className="app-row-label">Issues</span>
                      {Boolean(issueCount) && (
                        <span className="app-row-count" aria-label={`${String(issueCount)} open`}>{issueCount}</span>
                      )}
                    </button>

                    {workspaceChats.length > 0 && <span className="app-ws-divider" aria-hidden={true} />}

                    {workspaceChats.map((chat) => {
                      const isActive = view === 'chat' && activeChatId === chat.id;

                      if (renamingChatId === chat.id) {
                        return (
                          <InlineNameInput
                            key={chat.id}
                            initialValue={chat.title}
                            placeholder="Chat name"
                            icon={<IconChat aria-hidden={true} />}
                            onSubmit={(title) => {
                              props.onRenameChat(chat.id, title);
                              setRenamingChatId(null);
                            }}
                            onCancel={() => setRenamingChatId(null)}
                          />
                        );
                      }

                      return (
                        <div
                          key={chat.id}
                          className={`app-row app-chat-row${isActive ? ' active' : ''}${openMenu?.anchor.dataset.chatId === chat.id ? ' menu-open' : ''}`}
                        >
                          <button
                            type="button"
                            className="app-row-main"
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => props.onSelectChat(chat)}
                          >
                            <span className="app-row-icon"><IconChat aria-hidden={true} /></span>
                            <span className="app-row-label">{chat.title}</span>
                          </button>
                          <button
                            type="button"
                            className="app-row-action"
                            aria-label={`Options for ${chat.title}`}
                            aria-haspopup="menu"
                            data-chat-id={chat.id}
                            onClick={(event) => toggleMenu(event.currentTarget, [
                              {
                                label: 'Rename',
                                icon: <IconRename aria-hidden={true} />,
                                onSelect: () => setRenamingChatId(chat.id)
                              },
                              {
                                label: 'Delete',
                                icon: <IconTrash aria-hidden={true} />,
                                tone: 'danger',
                                onSelect: () => props.onDeleteChat(chat.id)
                              }
                            ])}
                          >
                            <IconMore aria-hidden={true} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="app-profile">
          {isSignedIn ? (
            <button
              type="button"
              className="app-profile-button"
              aria-haspopup="menu"
              aria-expanded={openMenu?.placement === 'above-stretch'}
              onClick={(event) => toggleMenu(event.currentTarget, [
                {
                  label: 'Settings',
                  icon: <IconSettings aria-hidden={true} />,
                  onSelect: props.onOpenSettings
                },
                {
                  label: 'Plans',
                  icon: <IconCard aria-hidden={true} />,
                  meta: props.planName,
                  onSelect: props.onOpenPlans
                },
                {
                  label: 'Log out',
                  icon: <IconLogOut aria-hidden={true} />,
                  onSelect: props.onLogout
                }
              ], 'above-stretch')}
            >
              <AppAvatar user={accountUser} login={githubLogin} />
              <span className="app-profile-copy">
                <strong>{displayName}</strong>
                {accountUser?.email && <span>{accountUser.email}</span>}
              </span>
            </button>
          ) : (
            <button type="button" className="app-profile-button" onClick={props.onLogin}>
              <span className="app-avatar muted" aria-hidden={true}><IconGithub aria-hidden={true} /></span>
              <span className="app-profile-copy">
                <strong>Log in</strong>
              </span>
            </button>
          )}
        </div>
      </div>

      {openMenu && (
        <AppMenu
          anchor={openMenu.anchor}
          items={openMenu.items}
          placement={openMenu.placement}
          onClose={() => setOpenMenu(null)}
        />
      )}
    </aside>
  );
}

export default AppSidebar;
